// People sign in through Supabase Auth (DESIGN-aiw.md: one auth,
// memberships decide the role). The CRM only ever holds the session JWT; reads
// run as the user and every write is a proposal.
import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import {clearCalendarCache} from "./calendar-cache";
import {clearProposalDrafts} from './proposal-draft';
import {clearNoticeDrafts} from './installation-notice-draft';
import { env } from "./env";
import { sessionToken } from "./session-token";
import { requestEmailLink } from "./email-signin";

let client: SupabaseClient | null = null;

export function supabase(): SupabaseClient | null {
  if (env.mock) return null;
  if (!client) client = createClient(env.supabaseUrl, env.supabaseAnonKey, { auth: { persistSession: true, autoRefreshToken: true } });
  return client;
}

/** See `session-token.ts`: the library refreshes; this never forces it (16 October 2026 sign-out loop). */
export async function getToken(): Promise<string | null> {
  const c = supabase();
  return c ? sessionToken(c.auth) : null;
}

export async function currentSession(): Promise<Session | null> {
  const c = supabase();
  if (!c) return null;
  const { data } = await c.auth.getSession();
  return data.session;
}

export async function signInWithMicrosoft(): Promise<void> {
  const c = supabase();
  if (!c) return;
  await c.auth.signInWithOAuth({ provider: "azure", options: { scopes: "email openid profile", redirectTo: `${window.location.origin}/` } });
}

/** Existing accounts only. Authentication does not itself grant CRM membership. */
export async function requestEmailSignIn(email: string): Promise<void> {
  const c = supabase();
  if (!c) throw new Error("Sign-in is unavailable");
  await requestEmailLink(c.auth, email, window.location.origin);
}

export async function signOut(): Promise<void> {
  try { clearProposalDrafts(window.sessionStorage); } catch { /* unavailable storage */ }
  try { clearNoticeDrafts(window.sessionStorage); } catch { /* unavailable storage */ }
  try { clearCalendarCache(window.sessionStorage); } catch { /* unavailable storage */ }
  await supabase()?.auth.signOut();
}
